"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import RestoreDraftModal from "./RestoreDraftModal";
import { getCurrentTimezoneOffset } from "../../../../utils/timezoneUtils";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";
import logger from "../../../../utils/logger";


const DESCRIPTION_MAX_LENGTH = 250;

export interface ListingData {
  title: string;
  category: "event" | "dining" | "wellness" | "adventure";
  dateTime: string;
  endTime?: string;
  isRecurring: boolean;
  recurrenceEndDate?: string; // ISO date string (YYYY-MM-DD)
  location: string;
  description: string;
  capacity: string;
  servicePrice?: string;
  status?: "PUBLISHED" | "UNPUBLISHED" | string;
  ticketTypes: Array<{
    id: string;
    name: string;
    price: string;
    limit: string;
    url?: string;
    ticketType?: string;
  }>;
  cancellationPolicy?: number;
  // Additional fields for API mapping
  buildingAddressId?: number;
  zoneId?: number;
  latitude?: number;
  longitude?: number;
  termsIds?: number[];
  privacyIds?: number[];
  uploadedImages?: Array<{
    id: string | number;
    file: File;
    url: string;
    name: string;
    size: number;
  }>;
  uploadedVideos?: string[];
  subCategoryId?: number;
  amenities?: number[];
  addons?: number[];
  currency?: string;
  spaceType?: string;
  duration?: string;
  size?: string;
  floorId?: number;
  instanceId?: number; // Instance ID for existing instances (PATCH/update)
  hostDisplayName?: string;
  hostDisplayImage?: string;
  minimumDuration?: number;
  maximumDuration?: number;
  bufferTimeInMinutes?: number;
  availabilitySlots?: Array<{
    day: string;
    startTime: string;
    endTime: string;
  }>;
  visibility?: "PUBLIC" | "PRIVATE";
  visibleToCorporate?: boolean;
  visibleToConsumer?: boolean;
  initialFloors?: any[]; // Cached floors from API
}

const CreateListingFlow = () => {
  const params: any = useParams();
  const location = useLocation();
  const isEditMode = useMemo(() => /\/host\/play-services\/edit\//.test(location.pathname), [location.pathname]);
  const spaceId = isEditMode ? Number(params.id) : undefined;
  const isPrefillingRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [publishing, setPublishing] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [hasCheckedForDraft, setHasCheckedForDraft] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get user's currency from their country setting
  const userCurrency = useUserCurrency();

  // Helper functions for URL management
  const getUrlParams = () => {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  };

  const updateUrl = (step: number) => {
    if (typeof window === "undefined") return;

    try {
      const params = new URLSearchParams();
      params.set("step", step.toString());
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
    } catch (e) {
      logger.error("Error updating URL:", e);
    }
  };

  const clearUrl = () => {
    if (typeof window === "undefined") return;
    window.history.replaceState({}, "", window.location.pathname);
  };

  // Get default initial state
  const getDefaultState = (): ListingData => ({
    title: "",
    category: "wellness",
    dateTime: "",
    endTime: "",
    isRecurring: false,
    recurrenceEndDate: undefined,
    location: "",
    description: "",
    capacity: "",
    ticketTypes: [],
    cancellationPolicy: undefined,
    buildingAddressId: undefined,
    termsIds: [],
    privacyIds: [],
    uploadedImages: [],
    uploadedVideos: [],
    subCategoryId: undefined,
    currency: "KES",
    spaceType: undefined,
    duration: undefined,
    size: "",
    floorId: undefined,
    instanceId: undefined,
    minimumDuration: 1,
    maximumDuration: 8,
    bufferTimeInMinutes: 15,
    availabilitySlots: [],
    visibility: "PUBLIC",
    visibleToCorporate: false,
    visibleToConsumer: false,
    initialFloors: [],
  });

  // Initialize state with default values (don't load from localStorage yet)
  const [data, setData] = useState<ListingData>(getDefaultState());

  // Set currency from user's country when available (create mode only)
  useEffect(() => {
    if (isEditMode || !userCurrency) return;

    // Only update if the current currency is the default "KES" and userCurrency is different
    setData(prev => {
      if (prev.currency === "KES" && userCurrency !== "KES") {
        logger.log("💰 [PlayServices] Setting currency from user country:", userCurrency);
        return { ...prev, currency: userCurrency };
      }
      return prev;
    });
  }, [userCurrency, isEditMode]);

  // Check for draft on mount and show restore modal (create mode only)
  useEffect(() => {
    if (hasCheckedForDraft || isEditMode) return;

    const urlParams = getUrlParams();
    const stepParam = urlParams.get("step");
    if (stepParam) {
      const step = parseInt(stepParam);
      if (step >= 1 && step <= 4) {
        setCurrentStep(step);
      }
    }

    // Check if there's a saved draft in localStorage
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("play-services-form-data");
      if (saved) {
        try {
          JSON.parse(saved);
          // Show restore modal if we have saved data
          setShowRestoreModal(true);
        } catch (e) {
          logger.error("Error parsing saved form data:", e);
          localStorage.removeItem("play-services-form-data");
        }
      }
    }

    setHasCheckedForDraft(true);
  }, [hasCheckedForDraft, isEditMode]);

  // Handle restore modal actions
  const handleContinueEditing = () => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("play-services-form-data");
      if (saved) {
        try {
          const parsedData = JSON.parse(saved);
          parsedData.description = (parsedData.description || "").slice(0, DESCRIPTION_MAX_LENGTH);
          setData(parsedData);
          setShowRestoreModal(false);
        } catch (e) {
          logger.error("Error restoring draft:", e);
          setShowRestoreModal(false);
        }
      }
    }
  };

  const handleStartFresh = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("play-services-form-data");
    }
    setData(getDefaultState());
    setCurrentStep(1);
    updateUrl(1);
    setShowRestoreModal(false);
  };

  // Clear form function
  const clearForm = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("play-services-form-data");
    }
    setData(getDefaultState());
    setCurrentStep(1);
    updateUrl(1);
    setLastSaved(null);
  };

  const updateData = (newData: Partial<ListingData>) => {
    setData(prev => {
      const updated = { ...prev, ...newData };
      if (newData.description !== undefined) {
        updated.description = (newData.description || "").slice(0, DESCRIPTION_MAX_LENGTH);
      }

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for debounced localStorage save (1 second) - skip while prefilling
      saveTimeoutRef.current = setTimeout(() => {
        if (typeof window !== "undefined" && !isPrefillingRef.current && !isEditMode) {
          localStorage.setItem("play-services-form-data", JSON.stringify(updated));
          setLastSaved(new Date());
        }
      }, 1000);

      return updated;
    });
  };

  const nextStep = () => {
    const newStep = Math.min(currentStep + 1, 4);
    setCurrentStep(newStep);
    updateUrl(newStep);
  };

  const prevStep = () => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
    updateUrl(newStep);
  };

  // Helper function to format size input (from host-add-space pattern)
  const formatSize = (sizeInput: string) => {
    if (!sizeInput) return `${data.capacity || 1} person space`;

    if (
      sizeInput.toLowerCase().includes("sqm") ||
      sizeInput.toLowerCase().includes("sq") ||
      sizeInput.toLowerCase().includes("ft") ||
      sizeInput.toLowerCase().includes("m²")
    ) {
      return sizeInput;
    }

    const numberMatch = sizeInput.match(/[\d.]+/);
    if (numberMatch) {
      return `${numberMatch[0]} sqm`;
    }

    return sizeInput;
  };

  // Helper function to get day of week from datetime string
  const getDayOfWeek = (dateTimeString: string) => {
    const days = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    const date = new Date(dateTimeString);
    return days[date.getDay()];
  };

  // Convert form data to API payload
  const convertToApiPayload = async (isDraft: boolean = false): Promise<any> => {
    // Process images - upload files and get URLs
    let processedImages: string[] = [];
    const imagesToUpload = (data.uploadedImages || []).filter((image: any) => image.file);

    if (imagesToUpload.length > 0) {
      try {
        logger.log("📸 [DEBUG] Uploading images for PLAY service:", imagesToUpload.length);
        const filesToUpload = imagesToUpload.map((image: any) => image.file);
        const { spaceManagementService } = await import("../../../../services/host-apiServices/spaceManagementService");
        const uploadResponse = await spaceManagementService.uploadMedia(filesToUpload);

        if (uploadResponse.status === 200 || uploadResponse.status === 201) {
          processedImages = uploadResponse.data;
          logger.log("📸 [DEBUG] Images uploaded successfully:", processedImages.length);
        } else {
          logger.error("📸 [DEBUG] Image upload failed:", uploadResponse);
          throw new Error("Failed to upload images");
        }
      } catch (error) {
        logger.error("📸 [DEBUG] Error uploading images:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        throw new Error(`Image upload failed: ${errorMessage}`);
      }
    }

    // Get existing image URLs (images that are already uploaded)
    const existingImages = (data.uploadedImages || [])
      .filter((image: any) => !image.file && image.url && !image.url.startsWith("blob:"))
      .map((image: any) => image.url);

    const sanitizedDescription = (data.description || "").trim().slice(0, DESCRIPTION_MAX_LENGTH);

    // Helper to generate ISO time string (HH:mm:ss) from HH:mm
    const generateTimeObject = (timeString: string) => {
      if (!timeString) return "09:00:00";
      const [hour, minute] = timeString.split(":").map(Number);
      const h = isNaN(hour) ? "09" : String(hour).padStart(2, "0");
      const m = isNaN(minute) ? "00" : String(minute).padStart(2, "0");
      return `${h}:${m}:00`;
    };

    const payload: any = {
      size: formatSize(data.size || "40 sqm"),
      media: {
        images: [...existingImages, ...processedImages],
        videos: data.uploadedVideos || [],
      },
      currency: data.currency || userCurrency || "KES",
      ratePrice: parseFloat(data.servicePrice || "0"),
      description: sanitizedDescription || "Amazing play experience",
      amenityIds: data.amenities || [],
      spaceType: data.spaceType || "HIGH", // Match user model default
      capacity: parseInt(data.capacity) || 20,
      duration: (data.duration || "HOURLY").toUpperCase(),
      visibility: (isDraft ? "PRIVATE" : data.visibility || "PUBLIC").toUpperCase(),
      visibleToCorporate: data.visibleToCorporate ?? false,
      visibleToConsumer: data.visibleToConsumer ?? false,
      addons: data.addons || [],
      ...(isEditMode ? {} : { numberOfInstances: 1 }),
      instances: [
        {
          // Include instance id for existing instances (PATCH/update)
          ...(data.instanceId ? { id: data.instanceId } : {}),
          // Use the Event Name from UI for instance name
          name: data.location || "",
          location: (() => {
            const loc: any = {
              name: data.location || "",
              buildingAddressId: data.buildingAddressId,
            };
            return loc;
          })(),
          // Include floorId if available (for PATCH/update)
          ...(data.floorId != null ? { floorId: data.floorId } : {}),
        },
      ],
      availabilitySlots: (data.availabilitySlots || []).map(slot => {
        let startTimeStr = "09:00";
        let endTimeStr = "17:00";

        if (typeof slot.startTime === "string") {
          startTimeStr = slot.startTime;
        } else if (slot.startTime && typeof slot.startTime === "object") {
          const hour = (slot.startTime as any).hour?.toString().padStart(2, "0") || "09";
          const minute = (slot.startTime as any).minute?.toString().padStart(2, "0") || "00";
          startTimeStr = `${hour}:${minute}`;
        }

        if (typeof slot.endTime === "string") {
          endTimeStr = slot.endTime;
        } else if (slot.endTime && typeof slot.endTime === "object") {
          const hour = (slot.endTime as any).hour?.toString().padStart(2, "0") || "17";
          const minute = (slot.endTime as any).minute?.toString().padStart(2, "0") || "00";
          endTimeStr = `${hour}:${minute}`;
        }

        return {
          day: slot.day,
          startTime: generateTimeObject(startTimeStr),
          endTime: generateTimeObject(endTimeStr),
        };
      }),
      minimumDuration: data.minimumDuration || 1,
      maximumDuration: data.maximumDuration || 8,
      bufferTimeInMinutes: Number(data.bufferTimeInMinutes || 0),
      subCategoryId: data.subCategoryId || 1,
      tickets: (data.ticketTypes || []).map(ticket => ({
        id: ticket.id && !isNaN(Number(ticket.id)) ? Number(ticket.id) : undefined,
        ticketType: ticket.ticketType || "OFFLINE",
        url: ticket.url || "",
        unitPrice: parseFloat(ticket.price || "0"),
        quantityLimit: parseInt(ticket.limit || "0"),
        currency: data.currency || userCurrency || "KES",
      })),
    };

    // Merge terms and privacy policies into termsIds as expected by the API
    const combinedTerms = [...(data.termsIds || []), ...(data.privacyIds || [])];

    if (combinedTerms.length > 0) {
      payload.termsIds = Array.from(new Set(combinedTerms)); // Deduplicate
    }

    if (typeof data.cancellationPolicy === "number") {
      payload.cancellationPolicyId = data.cancellationPolicy;
    }

    // Add event schedule if date/time specified (recurring or one-off)
    if (data.dateTime) {
      const timePart = data.dateTime.split("T")[1] || "09:00";

      const baseSchedule: any = {
        eventDate: data.dateTime.split("T")[0],
        recurring: !!data.isRecurring,
        dayOfWeek: getDayOfWeek(data.dateTime),
        startTime: generateTimeObject(timePart),
        endTime: generateTimeObject(data.endTime || "17:00"),
        recurrenceType: "DAILY", // Default as per model
      };

      if (data.isRecurring) {
        if (data.recurrenceEndDate && data.recurrenceEndDate.trim().length > 0) {
          baseSchedule.recurrenceEndDate = data.recurrenceEndDate;
        }
      }

      payload.eventSchedule = baseSchedule;
    }

    if (data.hostDisplayName && data.hostDisplayName.trim().length > 0) {
      payload.hostDisplayName = data.hostDisplayName.trim();
    }
    if (data.hostDisplayImage && data.hostDisplayImage.trim().length > 0) {
      payload.hostDisplayImage = data.hostDisplayImage.trim();
    }

    // Debug: Log availability slots format
    logger.log("✅ Availability slots being sent:", JSON.stringify(payload.availabilitySlots, null, 2));

    return payload;
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      const payload = await convertToApiPayload(false);

      logger.log("Publishing play service with payload:", payload);

      const timezone = getCurrentTimezoneOffset();
      const response = await import("../../../../services/host-apiServices/spaceManagementService").then(module =>
        isEditMode && spaceId
          ? module.spaceManagementService.updateSpace(spaceId, payload)
          : module.spaceManagementService.createSpace(payload, timezone)
      );

      logger.log("Play service published successfully:", response);

      // Clear localStorage and URL on successful publish
      if (typeof window !== "undefined") {
        localStorage.removeItem("play-services-form-data");
      }
      clearUrl();

      // Show success message
      const toastUtils = await import("../../../../utils/toast");
      toastUtils.default.success(
        isEditMode ? "Play service updated successfully!" : "Play service published successfully!"
      );

      // Redirect to host spaces with play tab
      setTimeout(() => {
        window.location.href = "/host/spaces?type=play";
      }, 1000);
    } catch (error) {
      logger.error("Error publishing play service:", error);
      const messageHandler = await import("../../../../utils/messageHandler");
      messageHandler.showApiMessage(error, isEditMode ? "update play service" : "publish play service");
    } finally {
      setPublishing(false);
    }
  };

  // Publish / Unpublish in edit mode
  const handleTogglePublish = async () => {
    if (!isEditMode || !spaceId) return;
    try {
      setPublishing(true);
      const mod = await import("../../../../services/host-apiServices/spaceManagementService");
      if (data.status === "PUBLISHED") {
        await mod.spaceManagementService.unpublishSpace(spaceId, 0 as any);
        setData(prev => ({ ...prev, status: "UNPUBLISHED" }));
        const toastUtils = await import("../../../../utils/toast");
        toastUtils.default.success("Play service unpublished");
      } else {
        await mod.spaceManagementService.publishSpace(spaceId, 0 as any);
        setData(prev => ({ ...prev, status: "PUBLISHED" }));
        const toastUtils = await import("../../../../utils/toast");
        toastUtils.default.success("Play service published");
      }
    } catch (e) {
      const messageHandler = await import("../../../../utils/messageHandler");
      messageHandler.showApiMessage(e, "toggle publish play service");
    } finally {
      setPublishing(false);
    }
  };

  // Prefill when in edit mode
  useEffect(() => {
    const prefill = async () => {
      if (!isEditMode || !spaceId) return;
      try {
        isPrefillingRef.current = true;
        const mod = await import("../../../../services/host-apiServices/spaceManagementService");
        const resp = await mod.spaceManagementService.getSpaceById(spaceId);
        const space = resp.data;

        // Map API space to ListingData
        const mapToListingData = (s: any): ListingData => {
          const images = (Array.isArray(s.media?.images) ? s.media.images : [])
            .map((u: any, idx: number) => {
              const url = typeof u === "string" ? u : u?.url;
              return url && typeof url === "string"
                ? { id: idx, file: undefined as any, url, name: `image-${idx}`, size: 0 }
                : null;
            })
            .filter(Boolean) as any[];

          // Handle both 'event' and 'eventSchedule' as the API structure can vary
          const eventInfo = s.event || s.eventSchedule || {};
          const eventDate = eventInfo.eventDate || "";

          const formatTime = (time: any) => {
            if (typeof time === "string") return time.substring(0, 5);
            if (time && typeof time === "object") {
              const h = (time.hour ?? 0).toString().padStart(2, "0");
              const m = (time.minute ?? 0).toString().padStart(2, "0");
              return `${h}:${m}`;
            }
            return "";
          };

          const startTimeStr = formatTime(eventInfo.startTime);
          const endTimeStr = formatTime(eventInfo.endTime);
          const dateTime = eventDate && startTimeStr ? `${eventDate}T${startTimeStr}` : "";

          // Handle both 'availableSlots' (GET response) and 'availabilitySlots' (alternative/POST structure)
          const sourceSlots = Array.isArray(s.availableSlots)
            ? s.availableSlots
            : Array.isArray(s.availabilitySlots)
              ? s.availabilitySlots
              : [];

          return {
            title: s.name || s.instances?.[0]?.title || "",
            category: "adventure",
            dateTime: dateTime,
            endTime: endTimeStr,
            isRecurring: !!eventInfo.recurring,
            recurrenceEndDate: eventInfo.recurrenceEndDate || undefined,
            location: s.instances?.[0]?.location?.name || "",
            description: (s.description || "").slice(0, DESCRIPTION_MAX_LENGTH),
            capacity: s.capacity != null ? String(s.capacity) : "",
            servicePrice: s.ratePrice != null ? String(s.ratePrice) : "",
            ticketTypes: Array.isArray(s.tickets)
              ? s.tickets.map((t: any) => ({
                  id: String(t.id ?? Math.random()),
                  name: t.name || "Standard",
                  price: t.unitPrice != null ? String(t.unitPrice) : "",
                  limit: t.quantityLimit != null ? String(t.limit ?? t.quantityLimit) : "",
                  url: t.url || "",
                  ticketType: t.ticketType || "OFFLINE",
                }))
              : [],
            cancellationPolicy:
              typeof s.cancellationPolicyId === "number" ? s.cancellationPolicyId : s.cancellationPolicy?.id,

            buildingAddressId:
              s.instances?.[0]?.location?.buildingAddress?.id || s.instances?.[0]?.location?.buildingAddressId,
            latitude: s.instances?.[0]?.location?.buildingAddress?.latitude || s.instances?.[0]?.location?.latitude,
            longitude: s.instances?.[0]?.location?.buildingAddress?.longitude || s.instances?.[0]?.location?.longitude,
            termsIds: Array.isArray(s.terms) ? s.terms.map((t: any) => t.id) : [],
            uploadedImages: images,
            uploadedVideos: [],
            subCategoryId: s.subCategory?.id,
            amenities: Array.isArray(s.amenities) ? s.amenities.map((a: any) => a.id || a) : [],
            addons: Array.isArray(s.addons) ? s.addons.map((a: any) => a.id || a) : [],
            currency: s.currency || undefined,
            spaceType: s.spaceType || undefined,
            duration:
              typeof s.duration === "string" && s.duration.trim().length > 0 ? s.duration.toUpperCase() : undefined,
            size: s.size || "",
            floorId: s.instances?.[0]?.floor?.id || s.instances?.[0]?.floorId,
            instanceId: s.instances?.[0]?.id, // Extract instance ID for PATCH/update
            hostDisplayName: s.hostDisplayName || "",
            hostDisplayImage: s.hostDisplayImage || "",
            minimumDuration: s.minimumDuration,
            maximumDuration: s.maximumDuration,
            bufferTimeInMinutes: s.bufferTimeInMinutes,
            availabilitySlots: sourceSlots.map((slot: any) => ({
              day: slot.day,
              startTime: formatTime(slot.startTime),
              endTime: formatTime(slot.endTime),
            })),
            visibility: s.visibility || "PUBLIC",
            visibleToCorporate: s.visibleToCorporate ?? false,
            visibleToConsumer: s.visibleToConsumer ?? false,
            initialFloors: s.instances?.[0]?.location?.buildingAddress?.floors || [],
          };
        };

        const prefilled = mapToListingData(space);
        setData(prefilled);
        setCurrentStep(1);
      } catch (e) {
        logger.error("Failed to prefill play service:", e);
      } finally {
        isPrefillingRef.current = false;
      }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, spaceId]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepOne
            data={data}
            updateData={updateData}
            onNext={nextStep}
            lastSaved={lastSaved}
            onClearForm={clearForm}
            isEditMode={isEditMode}
          />
        );
      case 2:
        return (
          <StepTwo
            data={data}
            updateData={updateData}
            onNext={nextStep}
            onPrev={prevStep}
            lastSaved={lastSaved}
            onClearForm={clearForm}
            isEditMode={isEditMode}
          />
        );
      case 3:
        return (
          <StepThree
            data={data}
            updateData={updateData}
            onNext={nextStep}
            onPrev={prevStep}
            lastSaved={lastSaved}
            onClearForm={clearForm}
            isEditMode={isEditMode}
          />
        );
      case 4:
        return (
          <StepFour
            data={data}
            updateData={updateData}
            onPrev={prevStep}
            publishing={publishing}
            onPublish={handlePublish}
            lastSaved={lastSaved}
            onClearForm={clearForm}
            isEditMode={isEditMode}
            onTogglePublish={handleTogglePublish}
          />
        );
      default:
        return (
          <StepOne
            data={data}
            updateData={updateData}
            onNext={nextStep}
            lastSaved={lastSaved}
            onClearForm={clearForm}
          />
        );
    }
  };

  return (
    <>
      {!isEditMode && (
        <RestoreDraftModal open={showRestoreModal} onContinue={handleContinueEditing} onStartFresh={handleStartFresh} />
      )}
      <div>{renderStep()}</div>
    </>
  );
};

export default CreateListingFlow;
